import axios from 'axios';

// Base URL for the API
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
// const API_URL = 'https://uat.mesight.dig-giv.com';

// Create an instance of axios with default settings
const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 10000, // Set a timeout for requests (10 seconds)
    headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': process.env.REACT_APP_API_KEY || '^FbWVMy)ex5/|>E|1Xy0KIW{e*@;dZS%8S`;0Y{wY8o*|2&MjV',
    },
});

/**
 * Fetch survey data from the backend API.
 * @param {number} surveyId - The ID of the survey to fetch.
 * @returns {Promise<Object>} - The survey data.
 */
const fetchSurveyData = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/`);
        console.log(response.data); // Log the fetched data
        return response.data; // Return the data for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for fetching survey:', error);
        if (error.response) {
            throw new Error(`Error: ${error.response.status}`);
        } else {
            throw new Error('Network error or request failed');
        }
    }
};

/**
 * Fetch samples for a specific survey.
 * @param {number} sampleID - The ID of the sample.
 * @returns {Promise<Array>} - The list of samples.
 */
const fetchSample = async (sampleID) => {
    try {
        // const response = await apiClient.get(`api/survey/${sampleID}/samples/`);
        const response = await apiClient.get(`api/samples/${sampleID}/`);
        console.log(response.data); // Log the fetched samples
        return response.data; // Return the samples for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for samples:', error);
        throw error;
    }
};

/**
 * Fetch implicit questions for a specific survey.
 * @param {number} surveyId - The ID of the survey.
 * @returns {Promise<Array>} - The list of implicit questions.
 */
const fetchImplicitQuestions = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/implicit-questions/`);
        console.log(response.data); // Log the fetched implicit questions
        return response.data; // Return the implicit questions for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for implicit questions:', error);
        if (error.response) {
            throw new Error(`Error: ${error.response.status}`);
        } else {
            throw new Error('Network error or request failed');
        }
    }
};

/**
 * Fetch explicit questions for a specific survey.
 * @param {number} surveyId - The ID of the survey.
 * @returns {Promise<Array>} - The list of explicit questions.
 */
const fetchExplicitQuestions = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/explicit-questions/`);
        console.log(response.data); // Log the fetched explicit questions
        return response.data; // Return the explicit questions for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for explicit questions:', error);
        if (error.response) {
            throw new Error(`Error: ${error.response.status}`);
        } else {
            throw new Error('Network error or request failed');
        }
    }
};


/**
 * Fetch roster and duplicate status for a specific survey/user combination.
 *
 * This endpoint now performs two tasks:
 *   1. Checks if the user already exists in the survey dataset 
 *      (unless the userId is a special bypass ID handled on the backend).
 *   2. If a duplicate is found, returns only `{ exists: true }`.
 *      Otherwise, returns roster details along with `{ exists: false }`.
 *
 * @param {number} surveyId - The ID of the survey.
 * @param {number} userId - The ID of the user.
 * @returns {Promise<Object>} Resolves with one of:
 *   - `{ exists: true }` if the user already exists and is blocked from continuing.
 *   - `{ survey_id, user_id, user_name, memories, exists: false }` if the user is allowed,
 *     or an error payload with `exists: false` if no roster entry is found.
 * @throws Will throw an error if the request fails at the network or server level.
 */
const fetchRoster = async (surveyId, userId) => {
    try {
        // await new Promise(resolve => setTimeout(resolve, 10000)); // Debug delay if needed
        const response = await apiClient.get(`api/survey/${surveyId}/roster/${userId}/`);
        console.log(response.data); // Log the fetched roster or exists flag
        return response.data;
    } catch (error) {
        console.error('There was a problem with the fetch operation for roster:', error);
        throw error;
    }
};


/**
 * Fetch languages for a specific survey.
 * @param {number} surveyId - The ID of the survey.
 * @returns {Promise<Array>} - The list of languages.
 */
const fetchLanguages = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/languages/`);
        console.log(response.data); // Log the fetched languages
        return response.data; // Return the languages for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for languages:', error);
        if (error.response) {
            throw new Error(`Error: ${error.response.status}`);
        } else {
            throw new Error('Network error or request failed');
        }
    }
};



/**
 * Fetch instructions for a specific survey.
 * @param {number} surveyId - The ID of the survey.
 * @returns {Promise<Array>} - The list of instructions.
 */
const fetchInstructions = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/instructions/`);
        console.log(response.data); // Log the fetched instructions
        return response.data; // Return the instructions for further use
    } catch (error) {
        console.error('There was a problem with the fetch operation for instructions:', error);
        if (error.response) {
            throw new Error(`Error: ${error.response.status}`);
        } else {
            throw new Error('Network error or request failed');
        }
    }
};

/**
 * Submit IAT results.
 * @param {number} surveyId - The ID of the survey.
 * @param {number} userId - The ID of the user.
 * @param {Array<Array>} attributes - The list of attributes to submit.
 * @returns {Promise<Object>} - The response data from the server.
 */
const submitIATResults = async(surveyId, userId, attributes) => {
    const requestBody = {
        survey_id: surveyId,
        user_id: userId,
        attributes: attributes,
    };

    try {
        const response = await apiClient.post('api/iatresult/', requestBody);
        return response.data;
    } catch (error) {
        console.error("error submitting IAT results", error);
        throw error;
    }
};


/**
 * Fetch demographic questions for a specific survey.
 * @param {number} surveyId - The ID of the survey.
 * @returns {Promise<Array>} - The list of demographic questions.
 */
const fetchdemographicQuestions = async (surveyId) => {
    try {
        const response = await apiClient.get(`api/survey/${surveyId}/demographic_questions/`);
        console.log(response.data); // Log the fetched demographic questions
        return response.data; // Return the demographic questions for further use
    } catch (error) {
        // Check if the error response exists and throw a specific error
        if (error.response) {
            // Server responded with a status other than 2xx
            throw new Error(`Error: ${error.response.status}`);
        } else {
            // Network error or other issues
            throw new Error('Network error or request failed');
        }
    }
};


/**
 * Submit demographics results.
 * @param {number} surveyId - The ID of the survey.
 * @param {number} userId - The ID of the user.
 * @param {Array<Array>} attributes - The list of attributes to submit.
 * @returns {Promise<Object>} - The response data from the server.
 */
const submitdemographicsResults = async(surveyId, userId, attributes) => {
    const requestBody = {
        survey_id: surveyId,
        user_id: userId,
        attributes: attributes,
    };

    try {
        const response = await apiClient.post('api/demographicresult/', requestBody);
        return response.data;
    } catch (error) {
        console.error("error submitting demographics results", error);
        throw error;
    }
};

/**
 * Submit explicit results.
 * @param {number} surveyId - The ID of the survey.
 * @param {number} userId - The ID of the user.
 * @param {Array<Array>} attributes - The list of attributes to submit.
 * @returns {Promise<Object>} - The response data from the server.
 */
const submitExplicitResults = async(surveyId, userId, attributes) => {
    const requestBody = {
        survey_id: surveyId,
        user_id: userId,
        attributes: attributes,
    };

    try {
        const response = await apiClient.post('api/explicitresult/', requestBody);
        return response.data;
    } catch (error) {
        console.error("error submitting explicit results", error);
        throw error;
    }
};


/**
 * Check if a specific user exists in a given survey.
 * @param {number} surveyId - The ID of the survey.
 * @param {number} userId - The ID of the user.
 * @returns {Promise<{exists: boolean}>} - Object with existence flag.
 */
const userExists = async (surveyId, userId) => {
    try {
        const response = await apiClient.get(`/api/survey/${surveyId}/user/${userId}/exists/`);
        console.log('User existence check:', response.data); 
        return response.data; // { exists: true/false }
    } catch (error) {
        console.error(`Error checking existence for user ${userId} in survey ${surveyId}:`, error);
        throw error;
    }
}


// Export the API functions
const api = {
    fetchSurveyData,
    fetchSample,
    fetchImplicitQuestions,
    fetchExplicitQuestions,
    fetchRoster,
    fetchLanguages,
    fetchInstructions,
    submitIATResults,
    fetchdemographicQuestions,
    submitdemographicsResults,
    submitExplicitResults,
    userExists,
};

export default api;
